import { useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';
import { useOffersT } from './offersI18n';
import { BANNER_ACCEPTED_TYPES, checkBannerFile, cropToBanner, meetsMinimumSize, readImageDimensions } from './bannerImage';
import { BANNER_TEMPLATES, fetchTemplateFile, type BannerTemplate } from './bannerTemplates';

export interface BannerValue {
  storageKey: string | null;
  url: string | null;
}

interface BannerUploaderProps {
  token: string;
  label: string;
  hint?: string;
  value: BannerValue;
  /** Called after a successful upload + confirm with the new storage key and a preview URL. */
  onChange: (value: BannerValue) => void;
  disabled?: boolean;
  /** Field error from validation or the API (already translated). */
  error?: string | null;
  testId?: string;
  /** Show the "Suggested banners" gallery (bundled text-free backgrounds). */
  suggestions?: boolean;
}

type Phase = 'idle' | 'processing' | 'uploading';

/**
 * Spec 062 — banner picker: drop zone + file input, jpeg/png/webp, ≤5 MB, min 1200×600,
 * canvas centre-crop to 2:1 (JPEG 0.85), upload with progress, preview, remove.
 * A failed upload keeps the previous banner (Flow A error path).
 * With `suggestions`, a gallery of bundled templates feeds the picked image through the same
 * check → crop → upload path as a user file (templates are already 2:1, so the crop is a no-op re-encode).
 */
function BannerUploader({ token, label, hint, value, onChange, disabled, error, testId, suggestions }: BannerUploaderProps) {
  const { t } = useOffersT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [localError, setLocalError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  /** Which template produced which storage key; selected only while that key is the current value. */
  const [templatePick, setTemplatePick] = useState<{ id: string; storageKey: string } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(id);
  }, [toast]);

  const busy = phase !== 'idle';

  const handleFile = async (file: File | undefined) => {
    if (!file || disabled || busy) return;
    await processFile(file, null);
  };

  const pickTemplate = async (template: BannerTemplate) => {
    if (disabled || busy) return;
    setLocalError(null);
    setPhase('processing');
    let file: File;
    try {
      file = await fetchTemplateFile(template);
    } catch {
      setLocalError(t('offers.banner.err.read'));
      setPhase('idle');
      return;
    }
    await processFile(file, template.id);
  };

  /** The one upload path for user files and templates: checks → 2:1 crop → upload-url + confirm. */
  const processFile = async (file: File, templateId: string | null) => {
    setLocalError(null);
    const typeOrSize = checkBannerFile(file);
    if (typeOrSize) {
      setLocalError(t(typeOrSize));
      setPhase('idle');
      return;
    }
    setPhase('processing');
    let cropped: File;
    try {
      const { width, height } = await readImageDimensions(file);
      if (!meetsMinimumSize(width, height)) {
        setLocalError(t('offers.banner.err.dimensions'));
        setPhase('idle');
        return;
      }
      cropped = await cropToBanner(file);
    } catch {
      setLocalError(t('offers.banner.err.read'));
      setPhase('idle');
      return;
    }
    setPhase('uploading');
    setProgress(0);
    try {
      const res = await api.uploadOfferBanner(cropped, token, (p) => setProgress(p.percentage));
      let preview = res.downloadUrl;
      if (!preview) {
        try { preview = URL.createObjectURL(cropped); } catch { preview = null; }
      }
      setTemplatePick(templateId ? { id: templateId, storageKey: res.storageKey } : null);
      onChange({ storageKey: res.storageKey, url: preview });
    } catch {
      setToast(t('offers.banner.uploadFailed'));
    } finally {
      setPhase('idle');
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const shownError = localError ?? error ?? null;
  const selectedTemplateId = templatePick && value.storageKey && templatePick.storageKey === value.storageKey ? templatePick.id : null;

  return (
    <div className="offer-banner-uploader" data-testid={testId}>
      <div className="offer-field-label">{label}</div>
      {hint && <p className="offer-field-hint">{hint}</p>}

      {value.storageKey ? (
        <div className="offer-banner-preview">
          {value.url
            ? <img src={value.url} alt={t('offers.banner.alt')} className="offer-banner-preview__img" />
            : <div className="offer-banner-preview__img offer-banner-preview__img--placeholder" />}
          <div className="offer-banner-preview__actions">
            <button type="button" className="offer-btn offer-btn--ghost" disabled={disabled || busy} onClick={() => inputRef.current?.click()}>
              {t('offers.banner.replace')}
            </button>
            <button
              type="button"
              className="offer-btn offer-btn--danger-ghost"
              disabled={disabled || busy}
              onClick={() => { setLocalError(null); onChange({ storageKey: null, url: null }); }}
            >
              {t('offers.banner.remove')}
            </button>
          </div>
        </div>
      ) : (
        <div
          className={`offer-dropzone${dragOver ? ' offer-dropzone--over' : ''}${disabled ? ' offer-dropzone--disabled' : ''}${shownError ? ' offer-dropzone--error' : ''}`}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled || busy}
          onClick={() => !disabled && !busy && inputRef.current?.click()}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !disabled && !busy) { e.preventDefault(); inputRef.current?.click(); } }}
          onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <circle cx="9" cy="10" r="1.6" />
            <path d="M21 16l-5-5-7 7" />
          </svg>
          <strong>{t('offers.banner.drop')}</strong>
          <span>{t('offers.banner.spec')}</span>
        </div>
      )}

      {suggestions && !disabled && (
        <div className="offer-banner-suggestions" data-testid={testId ? `${testId}-suggestions` : undefined}>
          <div className="offer-banner-suggestions__title" id={testId ? `${testId}-suggestions-title` : undefined}>
            {t('offers.banner.suggested.title')}
          </div>
          <p className="offer-field-hint">{t('offers.banner.suggested.hint')}</p>
          <ul className="offer-banner-suggestions__grid" aria-labelledby={testId ? `${testId}-suggestions-title` : undefined}>
            {BANNER_TEMPLATES.map((tpl) => {
              const name = t(tpl.labelKey);
              const selected = selectedTemplateId === tpl.id;
              return (
                <li key={tpl.id}>
                  <button
                    type="button"
                    className={`offer-banner-suggestion${selected ? ' offer-banner-suggestion--selected' : ''}`}
                    aria-label={t('offers.banner.suggested.pick', { name })}
                    aria-pressed={selected}
                    disabled={busy}
                    onClick={() => pickTemplate(tpl)}
                  >
                    <img src={tpl.src} alt="" className="offer-banner-suggestion__img" loading="lazy" />
                    <span className="offer-banner-suggestion__name">{name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={BANNER_ACCEPTED_TYPES.join(',')}
        className="offer-visually-hidden"
        aria-label={label}
        disabled={disabled || busy}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {phase === 'processing' && <div className="offer-upload-status">{t('offers.banner.processing')}</div>}
      {phase === 'uploading' && (
        <div className="offer-upload-status">
          <div className="offer-progress" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
            <div className="offer-progress__bar" style={{ width: `${progress}%` }} />
          </div>
          <span>{t('offers.banner.uploading', { p: progress })}</span>
        </div>
      )}
      {shownError && <div className="offer-field-error" role="alert">{shownError}</div>}
      {toast && <div className="partner-toast error" role="status">{toast}</div>}
    </div>
  );
}

export default BannerUploader;
